import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { navigateAction } from 'fluxible-router';
import { useDispatch } from 'react-redux';

import FluxibleContext from '../../../scripts/components/fluxible-context';
import { LOADING } from '../../../scripts/models/request-status';
import TextArea from '../../../scripts/components/common/inputs/textarea-input';
import TitleAndDescription from '../../../scripts/components/common/title-and-description';
import TextInputAdd from '../../../scripts/components/common/inputs/customs/text-input-add';
import { addWhitelistedUrl, saveHostOptions } from '../superAdminActions';
import { isHostOptionsJsonValid } from '../../../scripts/utils/json-validate';
import { openModal } from '../../../scripts/components/moderation-v2/comments-and-posts/modals/state/actions';
import { ModalType } from '../../../scripts/components/moderation-v2/comments-and-posts/modals';
import { ConfirmationModalProps } from '../../../scripts/components/moderation-v2/sharable/components/modals/ConfirmationModal';
import { createTwoButtonsRenderActions } from '../../../scripts/components/moderation-v2/sharable/components/modals/ConfirmationModal/confirmationModalUtils';

import { LazyLoadMargin } from './lazy-load-margin/LazyLoadMargin';
import { ResetExtractData } from './ResetExtractData';
import { EnableClaimDisqus } from './enable-claim-disqus/EnableClaimDisqus';
import { BlacklistedItem } from './BlacklistedItem';

import { useTypedSelector } from 'state';

interface SuperAdminProps {
  spotId: string;
  enableDisqusClaim: boolean;
}

export function SuperAdmin(props: SuperAdminProps) {
  const { spotId, enableDisqusClaim } = props;

  const { urls, hostOptions: initialHostOptions, saveHostOptionsStatus } = useTypedSelector(
    state => state.settings.superAdmin
  );

  const [urlInput, setUrlInput] = useState('');
  const [urlStatus, setUrlStatus] = useState('none');
  const [hostOptions, setHostOptions] = useState(initialHostOptions);

  const { executeAction } = useContext(FluxibleContext);

  const dispatch = useDispatch();

  useEffect(() => {
    setHostOptions(initialHostOptions);
  }, [initialHostOptions]);

  const onProductsClick = () => {
    const url = `/spot/${spotId}/preferences/super-admin/products`;
    executeAction(navigateAction, { url });
  };

  const onUrlAdd = () => {
    setUrlInput('');
    dispatch(addWhitelistedUrl(urlInput));
  };

  const onUrlChange = (value: string) => {
    const status = value.length > 0 ? 'success' : 'none';
    setUrlStatus(status);

    setUrlInput(value);
  };

  const onHostOptionsChange = (value: string) => {
    setHostOptions(value);
  };

  const onHostOptionsSave = () => {
    const jsonValidationResponse = isHostOptionsJsonValid(hostOptions);

    if (!jsonValidationResponse.state) {
      openHostOptionsSaveConfirmationModal();
    } else {
      onEditHostOptions();
    }
  };

  const onEditHostOptions = () => {
    dispatch(saveHostOptions({ spotId, value: hostOptions }));
  };

  const openHostOptionsSaveConfirmationModal = () => {
    const modalProps: ConfirmationModalProps = {
      description: 'The JSON Format is not valid are you sure you want to override the current JSON?',
      title: 'Warning',
      renderActions: createTwoButtonsRenderActions({
        primaryButtonLabel: 'OK',
        primaryButtonOnClick: onEditHostOptions
      })
    };

    dispatch(openModal({ modalType: ModalType.CONFIRM_GENERAL, modalProps }));
  };

  return (
    <div className="preferences-design super-admin">
      <div className={'dashboard-filter'}>
        <span className="filter active">Settings</span>
        <span className="filter" onClick={() => onProductsClick()}>
          Products
        </span>
      </div>
      <TextInputAdd
        title="Restricted Access"
        description="Limit site renderring to a specific URL."
        placeholder="Type a URL or regular expression…"
        onClick={onUrlAdd}
        onChange={onUrlChange}
        disabledAddButton={false}
        value={urlInput}
        status={urlStatus}
      />
      <div className={classNames('urls-list', { empty: urls.length === 0 })}>
        {urls.map((url, index) => (
          <BlacklistedItem key={index} url={url} spotId={spotId} />
        ))}
      </div>
      <div>
        <TitleAndDescription title="Host Options" />
        <TextArea value={hostOptions || ''} placeholder="host options..." onChange={onHostOptionsChange} />
        <button onClick={onHostOptionsSave} className="save-code-button">
          {saveHostOptionsStatus === LOADING ? <span className="spinner" /> : 'Save Host Options'}
        </button>
      </div>
      <div className="field">
        <EnableClaimDisqus spotId={spotId} value={enableDisqusClaim} />
        <ResetExtractData />
        <LazyLoadMargin spotId={spotId} />
      </div>
    </div>
  );
}
